/* eslint-disable class-methods-use-this */
import {
  ClientResponse,
  ConversationEventType,
  ConversationMessage,
  ConversationMessageStatus,
  SendConversationMessageParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessagePart,
  ConversationsApiDataMessagePartAttachment,
  ConversationsApiDataMessagePartText,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ApiMethods, ErrorResponse, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {emitMessagesWithAnimation, waitForAnimation} from './MessageAnimation';
import {PageContextNormalizer} from './PageContextNormalizer';
import {
  buildChannelMenuSubmissionPart,
  ConversationsApiChannelMenuSubmissionPartWithoutId,
  isChannelMenuSubmission,
} from './schema/ChannelMenuSubmissionParser';
import {
  buildCachedAnsweredOptionsPromptMessage,
  buildDeltaResponseState,
  emitAddedMessages,
  emitDeltaResponse,
  refetchConversationAndEmitDelta,
  selectedOptionIdFromSubmittedMessage,
} from './SendResponseHandling';

type DeleteConversationResponse =
  | {type: 'success'; data: undefined}
  | ErrorResponse;

type SendConversationMessageHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
    options?: {allowNoContent?: boolean},
  ) => ReturnType<ApiMethods['sendMessage']>;
  getConversation?: (
    conversationId: string,
  ) => ReturnType<ApiMethods['getConversation']>;
  deleteConversation?: (
    conversationId: string,
  ) => Promise<DeleteConversationResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  reportWarning?: ReportWarning;
  enableUhPendingInput?: boolean;
  attachmentDownloadPath?: string;
  getCachedMessage?: (
    conversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
};

type ConversationsApiTextPartWithoutId = Omit<
  ConversationsApiDataMessagePartText,
  'id'
>;

type ConversationsApiAttachmentPartWithoutId = Omit<
  ConversationsApiDataMessagePartAttachment,
  'id'
>;

type ConversationsApiMessagePartWithoutId =
  | ConversationsApiTextPartWithoutId
  | ConversationsApiAttachmentPartWithoutId
  | ConversationsApiChannelMenuSubmissionPartWithoutId;

export class SendConversationMessageHandler {
  private deps: SendConversationMessageHandlerDeps;

  constructor(deps: SendConversationMessageHandlerDeps) {
    this.deps = deps;
  }

  call = async (
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> => {
    if (!params.conversationId) {
      return {code: 400, error: 'Missing conversationId'};
    }
    if (!params.message.participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const payload = this.buildApiMessage(params.message);
    if (!payload) {
      return {
        code: 400,
        error: `Unsupported message type: ${params.message.type}`,
      };
    }

    const isMessagePartSubmission = this.isMessagePartSubmission(
      params.message,
    );
    const isNoContentMessagePartSubmission =
      this.isNoContentMessagePartSubmission(params.message, payload.parts);
    if (!isMessagePartSubmission) {
      this.emitOptimisticMessage(params);
      await waitForAnimation();
    }

    let response;
    try {
      response = isNoContentMessagePartSubmission
        ? await this.deps.sendMessage(params.conversationId, payload, {
            allowNoContent: true,
          })
        : await this.deps.sendMessage(params.conversationId, payload);
    } catch (e) {
      if (!isMessagePartSubmission) {
        this.emitMessageStatus(params, ConversationMessageStatus.Error);
      }
      throw e;
    }

    if (response.type === 'error') {
      if (
        isMessagePartSubmission &&
        SendConversationMessageHandler.shouldRefetchOnInvalidOption(response)
      ) {
        await refetchConversationAndEmitDelta({
          getConversation: this.deps.getConversation,
          emitConversationEvent: this.deps.emitConversationEvent,
          getCachedMessage: this.deps.getCachedMessage,
          conversationId: params.conversationId,
          participantsById: {
            [params.message.participant.id]: params.message.participant,
          },
          user: params.message.participant,
          attachmentDownloadPath: this.deps.attachmentDownloadPath,
          deleteConversation: this.deps.deleteConversation,
          reportWarning: this.deps.reportWarning,
        });
      } else if (isMessagePartSubmission && response.message) {
        this.emitMessagePartErrorUpdate(params, response.message);
      } else if (!isMessagePartSubmission) {
        this.emitMessageStatus(params, ConversationMessageStatus.Error);
      }
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    if (!isMessagePartSubmission) {
      const sentMessage = response.data?.messages.find(
        (message) => message.id === params.message.id,
      );
      this.emitMessageStatus(
        params,
        ConversationMessageStatus.Sent,
        sentMessage
          ? FrameworkAdapter.messageTimestamp(sentMessage.created)
          : undefined,
      );
    }

    if (!response.data) {
      return {code: 200};
    }

    const {context, ended, messages} = buildDeltaResponseState({
      conversationId: params.conversationId,
      apiMessages: response.data.messages,
      participantsById: {
        [params.message.participant.id]: params.message.participant,
      },
      user: params.message.participant,
      reportWarning: this.deps.reportWarning,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
    });
    const newMessages = messages.filter(
      // For message-part submissions, the user's own echo is suppressed
      // entirely; for plain sends, emittable user-owned messages are kept.
      (message) =>
        !isMessagePartSubmission ||
        message.participant.id !== params.message.participant.id,
    );
    const promptUpdate = isMessagePartSubmission
      ? buildCachedAnsweredOptionsPromptMessage({
          conversationId: params.conversationId,
          creatorPartId: params.message.data?.creator_part_id as
            | string
            | undefined,
          getCachedMessage: this.deps.getCachedMessage,
          participant: params.message.participant,
          selectedOptionId: selectedOptionIdFromSubmittedMessage(
            params.message,
          ),
        })
      : undefined;

    if (isMessagePartSubmission) {
      const addedMessageIds = new Set(newMessages.map((message) => message.id));
      this.emitExistingMessageUpdates(
        params,
        {
          messages: messages.reduce<Record<string, ConversationMessage>>(
            (messagesById, message) => {
              messagesById[message.id] = message;
              return messagesById;
            },
            {},
          ),
        },
        addedMessageIds,
      );
    }

    await emitDeltaResponse({
      emitConversationEvent: this.deps.emitConversationEvent,
      conversationId: params.conversationId,
      promptUpdate,
      context,
      ended,
      deleteConversation: this.deps.deleteConversation,
      typingEndedParticipantId: params.message.participant.id,
      reportWarning: this.deps.reportWarning,
      emitMessages: () =>
        isMessagePartSubmission
          ? emitAddedMessages(
              this.deps.emitConversationEvent,
              params.conversationId,
              newMessages,
            )
          : this.emitNewMessagesWithAnimation(
              params.conversationId,
              newMessages,
            ),
    });

    return {code: 200};
  };

  private emitExistingMessageUpdates(
    params: SendConversationMessageParams,
    conversation: {messages: Record<string, ConversationMessage>},
    addedMessageIds: Set<string>,
  ): void {
    const userId = params.message.participant?.id;
    Object.values(conversation.messages).forEach((message) => {
      if (
        message.participant.id === userId ||
        addedMessageIds.has(message.id)
      ) {
        return;
      }
      this.deps.emitConversationEvent({
        name: 'conversation-message-updated',
        conversationId: params.conversationId,
        participantId: message.participant.id,
        messageId: message.id,
        nonce: message.nonce,
        message,
      });
    });
  }

  private emitMessagePartErrorUpdate(
    params: SendConversationMessageParams,
    message: ConversationsApiDataConversation['messages'][number],
  ): void {
    const messages = FrameworkAdapter.fromApiMessages(
      params.conversationId,
      [message],
      {
        [params.message.participant.id]: params.message.participant,
      },
      params.message.participant,
      this.deps.attachmentDownloadPath,
      this.deps.reportWarning,
    );

    Object.values(messages).forEach((mappedMessage) => {
      this.deps.emitConversationEvent({
        name: 'conversation-message-updated',
        conversationId: params.conversationId,
        participantId: mappedMessage.participant.id,
        messageId: mappedMessage.id,
        nonce: mappedMessage.nonce,
        message: mappedMessage,
      });
    });
  }

  private emitOptimisticMessage(params: SendConversationMessageParams): void {
    this.deps.emitConversationEvent({
      name: 'conversation-message-added',
      conversationId: params.conversationId,
      participantId: params.message.participant.id,
      messageId: params.message.id,
      nonce: params.message.nonce,
      message: params.message,
    });
  }

  private async emitNewMessagesWithAnimation(
    conversationId: string,
    messages: ConversationMessage[],
  ): Promise<void> {
    await emitMessagesWithAnimation({
      conversationId,
      emitConversationEvent: this.deps.emitConversationEvent,
      messages,
      waitForCompletion: this.deps.enableUhPendingInput === true,
    });
  }

  private emitMessageStatus(
    params: SendConversationMessageParams,
    status: ConversationMessageStatus,
    timestamp = params.message.timestamp,
  ): void {
    const message: ConversationMessage = {
      ...params.message,
      timestamp,
      status,
    };
    this.deps.emitConversationEvent({
      name: 'conversation-message-updated',
      conversationId: params.conversationId,
      participantId: params.message.participant.id,
      messageId: params.message.id,
      nonce: params.message.nonce,
      message,
    });
  }

  private static shouldRefetchOnInvalidOption(
    response: ErrorResponse,
  ): boolean {
    return response.code === 400 && response.message === undefined;
  }

  private buildApiMessage(
    message: ConversationMessage,
  ): ConversationsApiSendMessageEndpointRequest | null {
    const parts = this.buildApiParts(message);
    if (!parts) {
      this.deps.reportWarning?.('Unsupported Conversations API message type', {
        tags: {message_type: message.type},
      });
      return null;
    }

    return {
      id: message.id,
      participant_id: message.participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        message.participant.role,
      ),
      parts,
      page_context: this.buildPageContext(message.data),
      ...(message.data?.creator_part_id
        ? {creator_part_id: message.data.creator_part_id as string}
        : {}),
      ...this.buildSubmissionMetadata(message),
    };
  }

  private buildSubmissionMetadata(
    message: ConversationMessage,
  ): Pick<ConversationsApiSendMessageEndpointRequest, 'data_with_pii'> {
    if (isChannelMenuSubmission(message)) {
      return {};
    }

    return {
      ...(message.data?.data_with_pii !== undefined
        ? {
            data_with_pii: message.data.data_with_pii as Record<
              string,
              unknown
            >,
          }
        : {}),
    };
  }

  private buildApiParts(
    message: ConversationMessage,
  ): ConversationsApiDataMessagePart[] | null {
    const channelMenuSubmissionPart = buildChannelMenuSubmissionPart(message);
    if (channelMenuSubmissionPart) {
      return this.assignPartIds([channelMenuSubmissionPart]);
    }

    if (this.isMessagePartSubmission(message)) {
      return [];
    }

    if (message.type === 'text-conversation-message') {
      if (this.isMessagePartSubmission(message) && message.text === '') {
        return [];
      }

      return this.assignPartIds([
        {
          type: 'text',
          content: {text: message.text},
        },
      ]);
    }

    if (message.type === 'attachment-conversation-message') {
      const part = this.buildAttachmentPart({attachment: message.attachment});
      return part ? this.assignPartIds([part]) : null;
    }

    if (message.type === 'text-with-attachments-conversation-message') {
      const textPart: ConversationsApiTextPartWithoutId = {
        type: 'text',
        content: {text: message.text},
      };
      const attachmentParts = message.attachments
        .map((attachment) => this.buildAttachmentPart({attachment}))
        .filter(
          (part): part is ConversationsApiAttachmentPartWithoutId =>
            part !== null,
        );
      return this.assignPartIds([textPart, ...attachmentParts]);
    }

    return null;
  }

  private isMessagePartSubmission(message: ConversationMessage): boolean {
    return typeof message.data?.creator_part_id === 'string';
  }

  private isNoContentMessagePartSubmission(
    message: ConversationMessage,
    parts: ConversationsApiDataMessagePart[],
  ): boolean {
    return (
      this.isMessagePartSubmission(message) &&
      !isChannelMenuSubmission(message) &&
      parts.length === 0
    );
  }

  private buildAttachmentPart({
    attachment,
  }: {
    attachment: {
      name: string;
      size: number;
      type: string;
      upload_token?: string;
    };
  }): ConversationsApiAttachmentPartWithoutId | null {
    if (!attachment.upload_token) {
      return null;
    }

    return {
      type: 'attachment',
      content: {
        text: attachment.name,
        name: attachment.name,
        size: attachment.size,
        attachment_type: attachment.type,
        upload_token: attachment.upload_token,
      },
    };
  }

  private assignPartIds(
    parts: ConversationsApiMessagePartWithoutId[],
  ): ConversationsApiDataMessagePart[] {
    return parts.map((part, index) => ({
      id: index + 1,
      ...part,
    })) as ConversationsApiDataMessagePart[];
  }

  private buildPageContext(
    data?: Record<string, unknown>,
  ): ConversationsApiSendMessageEndpointRequest['page_context'] {
    const candidate = PageContextNormalizer.extractPageContextCandidate(data);
    if (!candidate) {
      return undefined;
    }

    const pageContext =
      PageContextNormalizer.normalizePageContextForMessage(candidate);
    return Object.keys(pageContext).length > 0
      ? (pageContext as ConversationsApiSendMessageEndpointRequest['page_context'])
      : undefined;
  }
}
